import { Button } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GCard } from "~/components/GCard.jsx";
import { clubHasFlight } from "~/featureMatrix.jsx";
import { $t } from "~/i18n.js";
import { updateIfNeeded } from "~/lib/helpers.js";
import { api } from "~/lib/api.js";
import { getVliegerData } from "~/redux/persist/persist.actions.js";
import { BadgesModal } from "~/components/badges/BadgesModal.jsx";
import { getBadgeConfig } from "~/components/badges/BadgeConfig.js";
import { LevelProgress } from "~/components/badges/LevelProgress.jsx";
import { StackedBadge } from "~/components/badges/BadgeLevels.jsx";
import useMedia from "use-media";

export const BadgesWidget = () => {
  const BADGE_CONFIG = getBadgeConfig();
  const dispatch = useDispatch();
  const persist = useSelector((state) => state.persist);
  const pilot = persist?.vlieger_data;

  const nBadges = useMedia("(min-width: 800px)") ? 6 : 3;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const eligible =
    persist?.club?.has_badges &&
    pilot &&
    ["dbo", "solist"].includes(pilot.tag);
  const canFly = !!persist.profile?.vliegt && clubHasFlight();

  useEffect(() => {
    if (!eligible || !canFly) return;
    updateIfNeeded(persist.lastUpdatedData, async () => {
      dispatch(getVliegerData());
    });
  }, [dispatch, persist.lastUpdatedData, eligible]);

  useEffect(() => {
    if (!eligible || !canFly) return;
    setLoading(true);
    api
      .get("badges/user.json")
      .then((res) => setBadges(res || []))
      .finally(() => setLoading(false));
  }, [persist.profile?.vliegt, eligible]);

  if (!eligible || !canFly) return null;

  // earnedLevels per badge name: { name: [1, 2, ...] }
  const earnedByName = badges.reduce((acc, b) => {
    if (!acc[b.name]) acc[b.name] = [];
    acc[b.name].push(b.level);
    return acc;
  }, {});

  // 3 most recent unique badge names, or first 3 from config if none earned
  const allConfigKeys = Object.keys(BADGE_CONFIG);
  let recentNames = [...new Set(badges.map((b) => b.name))].slice(0, nBadges);

  // if recentNames has less than nBadges, fill with config keys (excluding already earned)
  if (recentNames.length < nBadges) {
    const needed = nBadges - recentNames.length;
    const toAdd = allConfigKeys
      .filter((k) => !recentNames.includes(k))
      .slice(0, needed);
    recentNames = recentNames.concat(toAdd);
  }

  const displayNames =
    recentNames.length > 0 ? recentNames : allConfigKeys.slice(0, nBadges);

  return (
    <GCard
      title={<span>{$t("Badges")}</span>}
      style={{ width: "100%", height: "100%" }}
      loading={loading || !persist.lastUpdatedData}
      extra={
        <Button type="link" onClick={() => setDrawerOpen(true)}>
          {$t("Bekijk alles")}
        </Button>
      }
    >
      <div style={{ marginBottom: 12 }}>
        <LevelProgress
          earnedCount={badges.length}
          size="small"
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 8,
          marginTop: 4,
        }}
      >
        {displayNames.map((name) => {
          const config = BADGE_CONFIG[name];
          if (!config) return null;
          const levels = earnedByName[name] || [];
          const maxLevel = levels.length > 0 ? Math.max(...levels) : 0;
          const isEarned = maxLevel > 0;
          return (
            <div
              key={name}
              onClick={() => setDrawerOpen(true)}
              style={{
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                padding: "6px 4px",
              }}
            >
              <StackedBadge
                icon={config.icon}
                label={config.label}
                count={isEarned ? maxLevel : 1}
                earned={isEarned}
                size={64}
                step={5}
              />
              <span
                style={{
                  fontSize: 11,
                  color: isEarned ? "#000" : "#aaa",
                  textAlign: "center",
                  lineHeight: 1.2,
                }}
              >
                {config.label}
              </span>
            </div>
          );
        })}
      </div>

      <BadgesModal
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        badges={badges}
      />
    </GCard>
  );
};
